import { Util } from "@auvious/common";
import { isSessionStoreDegraded, sessionStore } from "@auvious/utils";

import {
  KEY_AUTH_ATTEMPTS,
  KEY_AUTH_FLOW,
  KEY_OAUTH2_STATE_PREFIX,
  KEY_TAB_ID
} from "../core-ui.enums";

/**
 * how long a flow marker stays good for across the one gap its owner cannot renew it over: the
 * full-page navigation it makes once the authorization response arrives. only a marker that says
 * it is on that navigation gets this long, see isMarkerLive.
 */
export const AUTH_FLOW_LEASE_MS = 120 * 1000;

/**
 * how long a flow marker stays good for while the document that wrote it is supposed to be around
 * to renew it - which it does every AUTH_FLOW_RENEW_MS, so this is several missed renewals.
 *
 * it is deliberately far shorter than the lease above. a marker whose owner is gone - an agent who
 * reloaded while the sign-in window was up - is renewed by nobody, and the instances that follow
 * wait it out before they may run a flow of their own. waiting out two minutes leaves an agent
 * looking at a spinner, and then at a sign-in window the browser blocks because the wait spent
 * whatever user gesture we had.
 */
export const AUTH_FLOW_STALE_MS = 30 * 1000;

/**
 * how long we recognise an authorization state as one of ours. it has to cover the whole of an
 * interactive sign-in - a password to remember, a second factor, a federated login - because
 * failing to recognise our own response is what starts the flow over again.
 */
export const AUTH_STATE_MAX_AGE_MS = 10 * 60 * 1000;

/**
 * how long an instance waits for the flow another one is running before it checks on it again.
 * a safety net: the lease in the marker is what really decides whether that flow is still alive.
 */
export const AUTH_WAIT_TIMEOUT_MS = 150 * 1000;

/** the storage event carries us most of the time. this is the safety net */
export const AUTH_WAIT_POLL_MS = 500;

/** authorize attempts a tab may spend before we stop and ask the agent */
export const AUTH_MAX_ATTEMPTS = 3;

/** the window over which those attempts are counted */
export const AUTH_ATTEMPT_WINDOW_MS = 10 * 60 * 1000;

export type AuthFlowClaim =
  /** nobody else is on it: run the flow */
  | { role: "owner" }
  /** another instance is running one: wait for it to finish, then take a turn */
  | { role: "waiter" };

interface IAuthFlowMarker {
  at: number;
  state?: string;
  /**
   * the owner is on the full-page navigation the flow makes once the response arrives. it cannot
   * renew the marker across it, and the document that comes out the other side is the one that
   * lets go of it, so this is the only time a marker outlives the document that wrote it.
   */
  navigating?: boolean;
}

interface IAuthAttempts {
  count: number;
  firstAt: number;
}

function readJson<T>(key: string): T {
  try {
    return JSON.parse(sessionStore.getItem(key)) as T;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown) {
  try {
    sessionStore.setItem(key, JSON.stringify(value));
  } catch {}
}

function remove(key: string) {
  try {
    sessionStore.removeItem(key);
  } catch {}
}

/**
 * keeps the instances of the application that share a tab from running their authorization
 * flows on top of one another.
 *
 * a call centre can render our application several times in one tab - genesys does exactly that
 * with several interaction widgets - and every instance boots at the same time and asks for a
 * code of its own. left alone they hijack one another's sign-in window, take over one another's
 * authorization response and restart for ever, until the identity provider answers with
 * "too many redirects".
 *
 * every instance still signs in on its own and keeps an access token and a refresh token of its
 * own: the refresh tokens rotate and are single-use, so a token shared between instances would be
 * refreshed out from under all but one of them. what is coordinated is only *when* each instance
 * runs its flow - one at a time, through a leased marker in session storage, which is shared by
 * the same-origin frames of one tab and by the documents that follow them. the lease is what
 * makes that work across the owner's full-page navigation, where no lock could survive.
 */
export class AuthFlowCoordinator {
  /** has this instance written the marker that is out there? see releaseOnUnload */
  private ownsFlow = false;

  /**
   * is anything actually shared between the instances? when local/session storage is blocked,
   * `sessionStore` is a per-frame object in memory: there is nothing to coordinate through, so
   * the caller falls back to a flow per instance.
   */
  public isStorageShared(): boolean {
    return !isSessionStoreDegraded;
  }

  /**
   * the tab this instance lives in. the lock has to be scoped to it: locks are origin-wide, and
   * a second tab has a session storage of its own, so making it queue behind us would only keep
   * the agent waiting for nothing.
   */
  private tabId(): string {
    let id = sessionStore.getItem(KEY_TAB_ID);
    if (!id) {
      id = Util.uuidgen(Date.now().toString());
      try {
        sessionStore.setItem(KEY_TAB_ID, id);
      } catch {}
    }
    return id;
  }

  /**
   * decide who runs the flow under an exclusive lock, where the browser has one.
   *
   * the decision is a read-modify-write of one session storage entry. the frames of a tab share
   * an event loop and storage writes are synchronous, so it cannot interleave with itself there;
   * the lock is for the cross-process edge, and running without one is a safe fallback.
   */
  private async withAuthLock<T>(fn: () => T): Promise<T> {
    const locks = (navigator as any).locks;
    if (!locks?.request) {
      return fn();
    }
    try {
      return await locks.request(
        `auvious.auth.${this.tabId()}`,
        { mode: "exclusive" },
        fn
      );
    } catch {
      return fn();
    }
  }

  private readMarker(): IAuthFlowMarker {
    return readJson<IAuthFlowMarker>(KEY_AUTH_FLOW);
  }

  private isMarkerLive(marker: IAuthFlowMarker): boolean {
    if (!marker?.at) {
      return false;
    }

    const age = Date.now() - marker.at;

    // the long lease is only for the navigation the owner cannot renew across. anything else is
    // being renewed by a document that is still around, or by nobody at all.
    return age < (marker.navigating ? AUTH_FLOW_LEASE_MS : AUTH_FLOW_STALE_MS);
  }

  public claimFlow(): Promise<AuthFlowClaim> {
    return this.withAuthLock<AuthFlowClaim>(() => {
      if (this.isMarkerLive(this.readMarker())) {
        return { role: "waiter" };
      }

      this.writeMarker({ at: Date.now() });
      return { role: "owner" };
    });
  }

  /**
   * the flow is still going. push the lease out so the waiters keep waiting.
   *
   * `navigating` says we are about to leave this document for the one that finishes the flow, so
   * that nothing here reads the marker as ours to clean up and the longer lease applies.
   */
  public renewFlow(state?: string, navigating?: boolean) {
    this.writeMarker({ at: Date.now(), state, navigating });
  }

  private writeMarker(marker: IAuthFlowMarker) {
    writeJson(KEY_AUTH_FLOW, marker);

    if (!this.ownsFlow) {
      this.ownsFlow = true;
      window.addEventListener("pagehide", this.releaseOnUnload);
    }
  }

  /**
   * a flow marker is only renewed by the document that wrote it, so one left behind by a document
   * that is gone - an agent who reloaded while the sign-in window was up - would keep the
   * instances that follow waiting for a flow nobody is running. drop it on the way out.
   *
   * not when we are handing the flow over to the document we are navigating to: that navigation is
   * exactly what the lease is there to cover.
   */
  private releaseOnUnload = () => {
    if (!this.ownsFlow) {
      return;
    }

    if (!this.readMarker()?.navigating) {
      remove(KEY_AUTH_FLOW);
    }

    // whatever we had is behind us now. the instance that takes the turn next writes a marker of
    // its own, and this one is not ours to drop.
    this.forgetOwnership();
  };

  private forgetOwnership() {
    if (this.ownsFlow) {
      this.ownsFlow = false;
      window.removeEventListener("pagehide", this.releaseOnUnload);
    }
  }

  /**
   * let go of the flow, so that the next instance can take its turn.
   *
   * an instance that completes on a stored token, or fails to, is not necessarily the one that
   * holds the flow: another instance can be mid sign-in at that moment. given the `state` of the
   * flow being let go of, only a marker that belongs to that flow is dropped. without one the
   * marker is dropped regardless, for the paths where the caller is known to own it.
   *
   * `null` is not `undefined` here: it is what `URLSearchParams.get` hands back for a url with no
   * state, so it reaches us from a session that was had without an authorization response. such a
   * session never ran a flow, and must not drop the marker of the instance that is running one.
   */
  public abandonFlow(state?: string) {
    if (state !== undefined) {
      const marker = this.readMarker();
      if (marker && marker.state !== state) {
        return;
      }
    }
    remove(KEY_AUTH_FLOW);
    this.forgetOwnership();
  }

  /** is an instance of this tab running a flow right now? */
  public isFlowRunning(): boolean {
    return this.isMarkerLive(this.readMarker());
  }

  /**
   * wait for the instance that is running the flow to let go of it, or for its lease to run out.
   * resolves either way after `timeoutMs`; the caller claims again and finds out which it was.
   */
  public awaitFlowRelease(timeoutMs = AUTH_WAIT_TIMEOUT_MS): Promise<void> {
    return new Promise<void>((resolve) => {
      let settled = false;

      const settle = () => {
        if (settled) {
          return;
        }
        settled = true;
        clearInterval(poll);
        clearTimeout(timer);
        window.removeEventListener("storage", onStorage);
        resolve();
      };

      const check = () => {
        if (!this.isFlowRunning()) {
          settle();
        }
      };

      // fires on the sibling frames that share this storage area, and not on the one that wrote
      const onStorage = (event: StorageEvent) => {
        if (!event.key || event.key === KEY_AUTH_FLOW) {
          check();
        }
      };

      window.addEventListener("storage", onStorage);
      const poll = setInterval(check, AUTH_WAIT_POLL_MS);
      const timer = setTimeout(settle, timeoutMs);
    });
  }

  /**
   * remember that we issued this authorization state, so we recognise our own response later.
   */
  public registerIssuedState(state: string) {
    if (!state) {
      return;
    }
    this.sweepIssuedStates();
    writeJson(KEY_OAUTH2_STATE_PREFIX + state, Date.now());
  }

  public isIssuedState(state: string): boolean {
    if (!state) {
      return false;
    }
    const at = Number(sessionStore.getItem(KEY_OAUTH2_STATE_PREFIX + state));
    return !!at && Date.now() - at < AUTH_STATE_MAX_AGE_MS;
  }

  public forgetIssuedState(state: string) {
    if (state) {
      remove(KEY_OAUTH2_STATE_PREFIX + state);
    }
  }

  /** a flow that navigates elsewhere never gets to clear its own entry */
  private sweepIssuedStates() {
    try {
      const stale: string[] = [];
      for (let i = 0; i < sessionStore.length; i++) {
        const key = sessionStore.key(i);
        if (!key?.startsWith(KEY_OAUTH2_STATE_PREFIX)) {
          continue;
        }
        const at = Number(sessionStore.getItem(key));
        if (!at || Date.now() - at >= AUTH_STATE_MAX_AGE_MS) {
          stale.push(key);
        }
      }
      stale.forEach(remove);
    } catch {}
  }

  /**
   * spend one of this tab's authorize attempts. false means we have spent them all and must stop
   * asking the identity provider - it is what keeps a failing flow from looping into a
   * "too many redirects".
   *
   * the budget is per tab rather than per instance on purpose: an instance has no identity that
   * survives the full-page navigation the flow goes through. a completed flow gives the budget
   * back, so instances taking turns never run out of it - only ones that keep failing do.
   *
   * what counts is a round trip that actually started: a sign-in window that opened, or a
   * redirect that is about to happen. a blocked window and a retry the agent clicked do not.
   */
  public consumeAttempt(): boolean {
    const { count, firstAt } = this.attemptsInWindow();

    if (count >= AUTH_MAX_ATTEMPTS) {
      return false;
    }

    writeJson(KEY_AUTH_ATTEMPTS, {
      count: count + 1,
      firstAt
    } as IAuthAttempts);
    return true;
  }

  /**
   * may this tab still ask? checked before a sign-in window is opened, and the attempt is spent
   * only once it is: a window the browser blocked asked for nothing and is not one.
   */
  public hasAttemptsLeft(): boolean {
    return this.attemptsInWindow().count < AUTH_MAX_ATTEMPTS;
  }

  private attemptsInWindow(): IAuthAttempts {
    const now = Date.now();
    const spent = readJson<IAuthAttempts>(KEY_AUTH_ATTEMPTS);
    const withinWindow =
      spent?.firstAt && now - spent.firstAt < AUTH_ATTEMPT_WINDOW_MS;
    return {
      count: withinWindow ? spent.count : 0,
      firstAt: withinWindow ? spent.firstAt : now
    };
  }

  public resetAttempts() {
    remove(KEY_AUTH_ATTEMPTS);
  }
}
